import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
    QueryList,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import {
    ControlContainer,
    FormArray,
    FormGroup,
    UntypedFormGroup,
} from '@angular/forms';
import { NgbAccordionDirective } from '@ng-bootstrap/ng-bootstrap';
import {
    AccordionPanelChangeEvent,
    FieldDefinition,
    FormFieldType,
    TranslationsService,
    WdxDestroyClass,
} from '@wdx/shared/utils';
import { debounceTime, takeUntil } from 'rxjs/operators';
import {
    FormFrameworkEvent,
    ILayoutAndSchemaFieldDefinition,
    ReactiveFormElementChild,
    ReactiveFormLayoutAndDefinition,
    ValidationSummaryError,
    ValidationSummarySection,
} from '../../../models';
import { FormStaticService, FormValidationService } from '../../../services';
import { Subscription } from 'rxjs';
import { BespokeValidators } from '../../../validators';
import { FormArrayService } from '../../../services/form-array/form-array.service';
import { FormFirstLevelService } from '../../../services/form-builder/form-first-level.service';

@Component({
    selector: 'wdx-ff-form-array',
    templateUrl: './form-framework-form-array.component.html',
    standalone: false,
})
export class FormFrameworkFormArrayComponent
    extends WdxDestroyClass
    implements OnInit, AfterViewInit
{
    private translationsService = inject(TranslationsService);
    public formArrayService = inject(FormArrayService);
    public firstLevel = inject(FormFirstLevelService).firstLevel;
    @Input() entityId!: string;
    private _elementDefinition!: ILayoutAndSchemaFieldDefinition;
    @Input() set elementDefinition(value: ILayoutAndSchemaFieldDefinition) {
        this._elementDefinition = value;
        this.disabled = Boolean(
            this.elementDefinition?.isReadOnly ||
                this.elementDefinition?.isDisabled,
        );
        if (this.afterViewInit) {
            this.disableInactiveControls();
        }
    }
    get elementDefinition() {
        return this._elementDefinition;
    }
    @Input() formContext!: any;
    @Input() parentData!: any;
    @Input() readonlyMode?: boolean;

    @Output() formEvent = new EventEmitter<FormFrameworkEvent>();

    disabled = false;
    openPanels = [];
    openChildPanels = [];

    validationStatus!: boolean;
    validationSummary: ValidationSummarySection[] = [];
    @ViewChildren('subForm', { read: ElementRef })
    subForms!: QueryList<ElementRef>;
    @ViewChildren('grandChildForm', { read: ElementRef })
    grandChildForms!: QueryList<ElementRef>;

    @ViewChild(NgbAccordionDirective) accordion!: NgbAccordionDirective;
    @ViewChildren('panelToggle') panelToggles!: QueryList<ElementRef>;

    private exclusiveChildItems?: FieldDefinition[];
    private exclusiveSubs?: Subscription[];
    private exclusiveIsPrimaryValidators: Record<string, () => void> = {};
    private formArray?: FormArray;
    private formGroups?: FormGroup[];
    private afterViewInit = false;

    readonly FormFieldType = FormFieldType;
    readonly VALIDATION_SUMMARY_MESSAGE =
        'Section contains missing values or validation errors';

    constructor(
        public controlContainer: ControlContainer,
        private formValidationService: FormValidationService,
        private formStaticService: FormStaticService,
        private cd: ChangeDetectorRef,
    ) {
        super();
    }

    ngOnInit(): void {
        this.formArray = this.controlContainer?.control as FormArray;
        this.formGroups = this.formArray.controls as FormGroup[];

        this.formStaticService.submitAttempted$.subscribe(() => {
            this.calculateValidationErrors();
            this.controlContainer.control?.statusChanges
                .pipe(takeUntil(this.destroyed$), debounceTime(200))
                .subscribe(() => {
                    this.calculateValidationErrors();
                });
        });
        this.exclusiveChildItems = this.elementDefinition?.childSchema?.filter(
            ({ isExclusive }) => isExclusive,
        );
        if (this.exclusiveChildItems?.length) {
            this.setupExclusiveListeners();
        }
    }

    ngAfterViewInit(): void {
        this.afterViewInit = true;
        this.disableInactiveControls();

        this.subForms.changes
            .pipe(takeUntil(this.formStaticService.destroyed$))
            .subscribe(() => this.disableInactiveControls());

        this.cd.detectChanges();
        if (this.subForms.length === 1) {
            this.accordion.expandAll();
        }
    }

    calculateValidationErrors(): void {
        this.validationStatus = !!this.controlContainer.control?.invalid;
        this.validationSummary = this.formValidationService.getArrayErrors(
            this.elementDefinition?.children as ReactiveFormElementChild[],
            (this.controlContainer.control as any)
                .controls as UntypedFormGroup[],
        );
        this.cd.detectChanges();
    }

    disableAllControls() {
        this.disableControls(true);
    }

    disableInactiveControls() {
        const subformDef = this.elementDefinition?.children?.[0]
            ?.layoutAndDefinition?.[0] as ReactiveFormLayoutAndDefinition[];
        if (!Array.isArray(subformDef) || subformDef.length === 0) {
            return;
        }
        const availableKeys = subformDef?.map((def) => def.name) as string[];

        this.disableControls(false, availableKeys);
    }

    disableControls(allControls: boolean, availableKeys?: string[]) {
        (this.controlContainer.control as any).controls.forEach(
            (formGroup: UntypedFormGroup) => {
                Object.entries(formGroup.controls).forEach(
                    ([name, control]) => {
                        if (!availableKeys?.includes(name) || allControls) {
                            control.disable({ emitEvent: false });
                        }
                    },
                );
            },
        );
    }

    panelChange(panelId: string, collapsed: boolean): void {
        this.addRemoveFromNgPanelArray({ panelId, collapsed }, 'openPanels');
    }

    panelChildChange(panelId: string, collapsed: boolean): void {
        this.addRemoveFromNgPanelArray(
            { panelId, collapsed },
            'openChildPanels',
        );
    }

    addRemoveFromNgPanelArray(
        props: AccordionPanelChangeEvent,
        panelArrayVar: string,
    ): void {
        const STR_ARR = props.panelId.split('-');
        const TYPE = STR_ARR[STR_ARR.length - 2];
        const INDEX = `${TYPE}-${STR_ARR[STR_ARR.length - 1]}`;

        if (!props.collapsed) {
            (this as any)[panelArrayVar].push(INDEX);
            (this as any)[panelArrayVar] = [...(this as any)[panelArrayVar]];
        }

        if (props.collapsed) {
            (this as any)[panelArrayVar] = [
                ...(this as any)[panelArrayVar].filter(
                    (panelIndex: string) => panelIndex !== INDEX,
                ),
            ];
        }
    }

    addToArray(
        elementDefinition: ILayoutAndSchemaFieldDefinition,
        useFormData?: boolean,
        newIndex?: number,
    ) {
        this.markFormAsDirty();
        this.formArrayService.addToArray(elementDefinition, {
            useFormData,
            newIndex,
        });
        const addedIndex =
            this.firstLevel[elementDefinition.name as string]?.formArray
                ?.controls?.length - 1;

        const addPanelId = `ngb-panel-${elementDefinition.name}-child-${addedIndex}`;
        this.cd.detectChanges();
        this.accordion.expand(addPanelId);
        this.panelToggles.last.nativeElement.focus();
        this.setupExclusiveListeners();
    }

    cloneItem(
        elementDefinition: ILayoutAndSchemaFieldDefinition,
        formGroup: FormGroup,
    ) {
        const value: any = { identifier: null };
        const newIndex = (formGroup.parent as FormArray).length;

        elementDefinition.sectionLayoutDefinitions?.[0]?.elementLayoutDefinitions
            ?.filter(
                ({ name }) =>
                    name &&
                    !elementDefinition?.childSchema?.find(
                        (y) => y.name === name,
                    )?.isExclusive,
            )
            .forEach(({ name }) => {
                value[name] = structuredClone(formGroup.value[name]);
            });

        const subFormName = elementDefinition?.name as string;
        const parentFormControlValue =
            this.formStaticService.form.value[subFormName];
        // sync formData to current form values - needed to ensure that all form controls are properly initialised
        this.formStaticService.formData.data[subFormName] = structuredClone(
            parentFormControlValue,
        );
        const formDataParent =
            this.formStaticService.formData.data[subFormName];

        if (Array.isArray(formDataParent)) {
            if (formDataParent[newIndex]) {
                formDataParent[newIndex] = value;
            } else {
                formDataParent.push(value);
            }
        }
        this.addToArray(elementDefinition, true, newIndex);
    }

    removeFromArray(
        elementDefinition: ILayoutAndSchemaFieldDefinition,
        index: number,
    ) {
        this.markFormAsDirty();
        this.formArrayService.removeFromArray(elementDefinition, { index });
        this.setupExclusiveListeners();
    }

    onErrorClicked(
        error: ValidationSummaryError,
        index: number,
        grandChildIndex?: number,
    ): void {
        const selector = `[data-form-control='${error.name}']`;
        const scrollToEl = grandChildIndex
            ? this.grandChildForms
                  .get(grandChildIndex)
                  ?.nativeElement.querySelector(selector)
            : this.subForms.get(index)?.nativeElement.querySelector(selector);
        this.formValidationService.scrollToError(scrollToEl);
    }

    private markFormAsDirty() {
        // settimeout required to ensure dirty state isn't reset before form changed event is emitted
        setTimeout(() => {
            this.formStaticService.form.markAsDirty();
        }, 0);
    }

    private setupExclusiveIsRequiredValidation() {
        const layoutIsRequired: Record<string, boolean> = {};

        this.elementDefinition?.sectionLayoutDefinitions?.[0]?.elementLayoutDefinitions?.forEach(
            ({ name, isRequired }) => {
                layoutIsRequired[name as string] = isRequired ?? false;
            },
        );
        this.exclusiveChildItems
            ?.filter((eci) => eci?.name)
            .forEach(({ name, isRequired: fieldDefIsRequired }) => {
                const isRequired =
                    fieldDefIsRequired || layoutIsRequired[name as string];
                this.exclusiveIsPrimaryValidators[name as string] = () => {
                    const exclusiveRequiredChildControls = this.formGroups?.map(
                        ({ controls }) => controls[name as string],
                    );

                    if (!exclusiveRequiredChildControls?.length) {
                        return;
                    }

                    if (exclusiveRequiredChildControls.length === 1) {
                        exclusiveRequiredChildControls[0].addValidators([
                            BespokeValidators.isTrue(),
                        ]);
                        exclusiveRequiredChildControls[0].updateValueAndValidity(
                            { onlySelf: true, emitEvent: false },
                        );
                        return;
                    }

                    const currentState = exclusiveRequiredChildControls?.every(
                        ({ value }) => !value,
                    )
                        ? 'noneSet'
                        : exclusiveRequiredChildControls?.every(({ value }) =>
                                Boolean(value),
                            )
                          ? 'allSet'
                          : 'oneSet';

                    switch (currentState) {
                        case 'noneSet':
                            {
                                if (isRequired) {
                                    exclusiveRequiredChildControls?.forEach(
                                        (control) =>
                                            control.setErrors({
                                                requiredExclusive:
                                                    this.translationsService.translateTokenisedString(
                                                        'FORM_VALIDATION_ERROR_EXCLUSIVE_NONE',
                                                    ),
                                            }),
                                    );
                                }
                            }
                            break;
                        case 'allSet':
                            {
                                exclusiveRequiredChildControls?.forEach(
                                    (control) =>
                                        control.setErrors({
                                            requiredExclusive:
                                                this.translationsService.translateTokenisedString(
                                                    'FORM_VALIDATION_ERROR_EXCLUSIVE_ALL',
                                                ),
                                        }),
                                );
                            }
                            break;
                        default:
                            {
                                exclusiveRequiredChildControls?.forEach(
                                    (control) => control.setErrors(null),
                                );
                            }
                            break;
                    }
                };
            });
    }

    private setupExclusiveListeners() {
        this.removeExisitingValidatorsExclusiveControls();
        this.setupExclusiveIsRequiredValidation();
        this.exclusiveSubs?.forEach((sub) => sub?.unsubscribe());
        this.exclusiveSubs = this.exclusiveChildItems
            ?.flatMap(({ name }) =>
                (this.formGroups as FormGroup[]).map(({ controls }) => ({
                    control: controls[name as string],
                    name,
                })),
            )
            .map(({ control, name }, i) =>
                control?.valueChanges
                    ?.pipe(takeUntil(this.destroyed$))
                    .subscribe((value) => {
                        const controls = this.formGroups?.map(
                            ({ controls }) => controls[name as string],
                        );
                        if (value) {
                            controls?.forEach((ctl, index) => {
                                if (ctl.value && index !== i) {
                                    ctl.setValue(false);
                                }
                            });
                        }
                        this.exclusiveIsPrimaryValidators[name as string]?.();
                    }),
            );

        setTimeout(() => {
            Object.values(this.exclusiveIsPrimaryValidators).forEach(
                (validator) => validator(),
            );
        }, 0);
    }

    private removeExisitingValidatorsExclusiveControls() {
        this.exclusiveChildItems?.flatMap(({ name }) =>
            this.formGroups?.forEach(({ controls }) => {
                controls[name as string]?.clearValidators();
                controls[name as string]?.updateValueAndValidity();
            }),
        );
    }
}
